import {
  CalculatedDiploma,
  Course,
  ExtraPoints,
  UserDetails,
} from '@/data/models'

import { isImprovementSet } from '@/hooks/useCalculations'
import * as Sentry from '@sentry/react'

// Calculate age
export const calculateAgeWhenStudyStart = (
  studyStartYear: number,
  birthyear: number
) => {
  const year = studyStartYear

  const age = year - birthyear

  return age
}

// Calculate grade points
export const calculateGradePoints = (courses: Course[]) => {
  let gradePoints = 0
  const gradesSum =
    courses.reduce(function (prev, current) {
      return prev + +current.grade
    }, 0) +
    courses.reduce(function (prev, current) {
      return prev + +current.exam
    }, 0) +
    courses.reduce(function (prev, current) {
      return prev + +current.exam2
    }, 0)
  let numCourses = 0
  courses.forEach((course) => {
    if (course.exam > 0) {
      numCourses += 1
    }
    if (course.exam2 > 0) {
      numCourses += 1
    }
    if (course.grade > 0) {
      numCourses += 1
    }
  })
  const grades = (gradesSum / numCourses) * 10
  const gradesRounded = Math.round(grades * 10) / 10
  if (courses.length <= 0) {
    gradePoints = 0
  } else {
    gradePoints = gradesRounded
  }
  if (numCourses <= 0) {
    return { gradePoints: 0, numCourses: numCourses }
  } else {
    return { gradePoints: gradePoints, numCourses: numCourses }
  }
}

// Calculate age points
export const calculateAgePoints = (age: number, studyStartYear: number) => {
  if (studyStartYear >= 2028) {
    return { normal: 0, old: 0 }
  }

  const agepoints = { normal: 0, old: 0 }

  agepoints.normal = (age - 19) * 2
  agepoints.old = (age - 23) * 2

  if (agepoints.normal > 8) {
    agepoints.normal = 8
  } else if (agepoints.normal < 0) {
    agepoints.normal = 0
  }

  if (agepoints.old > 8) {
    agepoints.old = 8
  } else if (agepoints.old < 0) {
    agepoints.old = 0
  }

  return agepoints
}

// Calculate extra points
export const calculateExtraPoints = (extraPoints: ExtraPoints) => {
  let points = 0

  if (extraPoints['30 studiepoeng']) {
    points = 1
  }

  if (
    extraPoints['60 studiepoeng'] ||
    extraPoints.Folkehøgskole ||
    extraPoints['Militær/siviltjeneste']
  ) {
    points = 2
  }

  return points
}

// Calculate science points
export const calculateSciencePoints = (courses: Course[]) => {
  let sciencePoints = 0
  let mathSciencePoints = 0
  let otherSciencePoints = 0
  courses.forEach((course) => {
    if (course.grade > 1 || course.exam > 1) {
      if (course.mathSciencepoints) {
        mathSciencePoints += course.sciencepoints
      } else {
        otherSciencePoints += course.sciencepoints
      }
    }
  })

  if (mathSciencePoints > 1.5) {
    mathSciencePoints = 1.5
  }

  sciencePoints = mathSciencePoints + otherSciencePoints

  if (sciencePoints > 4) {
    sciencePoints = 4
  }

  return sciencePoints
}

// Calculate language points
export const calculateLanguagePoints = (courses: Course[]) => {
  let languagePoints = 0
  courses.forEach((course) => {
    if (course.grade > 1 || course.exam > 1) {
      languagePoints += course.languagepoints
    }
  })

  if (languagePoints > 4) {
    languagePoints = 4
  }

  return languagePoints
}

// Calculate total extra course points which can be maximum 4
export const calculateTotalScienceAndLanguagePoints = (
  languagePoints: number,
  sciencePoints: number
) => {
  let totalExtraCoursePoints = 0
  totalExtraCoursePoints = languagePoints + sciencePoints

  if (totalExtraCoursePoints > 4) {
    totalExtraCoursePoints = 4
  }

  return totalExtraCoursePoints
}

// Calculate total points
export const calculateTotalPoints = (points: CalculatedDiploma) => {
  const totalpoints = { first: 0, normal: 0, old: 0 }

  totalpoints.first = points.grades + points.totalScienceAndLanguage
  totalpoints.normal =
    points.grades +
    points.extra +
    points.totalScienceAndLanguage +
    points.age_normal
  totalpoints.old =
    points.grades + points.extra + points.science + points.age_235

  return totalpoints
}

export const improvedCourses = (courses: Course[]) =>
  courses.map((course) => {
    if (!isImprovementSet(course)) {
      return course
    }

    return {
      ...course,
      grade: course.improved,
      exam: course.hasTwoExams ? course.improved2 : 0,
      exam2: 0,
    }
  })

const calculator = (
  courses: Course[],
  userDetails: UserDetails,
  extraPoints: ExtraPoints,
  diploma: string,
  improved: boolean
) => {
  return Sentry.startSpan(
    {
      op: 'function',
      name: 'Calculate Points',
    },
    (span) => {
      const result = {
        diploma: diploma,
        improved: improved,
        age: 0,
        numCourses: 0,
        grades: 0,
        average: 0,
        grades_improved: 0,
        science: 0,
        language: 0,
        totalScienceAndLanguage: 0,
        age_normal: 0,
        age_235: 0,
        extra: 0,
        total_first: 0,
        total_normal: 0,
        total_improved: 0,
        total_235: 0,
      }

      span.setAttribute('diploma_type', diploma)
      span.setAttribute('improved_mode', improved)
      span.setAttribute('num_courses', courses.length)

      const coursesToCalculate = improved ? improvedCourses(courses) : courses

      // Calculate age
      result.age = calculateAgeWhenStudyStart(
        userDetails.studyStartYear ?? new Date().getFullYear(),
        userDetails.birthyear ?? 0
      )

      // Calculate grade points
      const gradePoints = calculateGradePoints(coursesToCalculate)
      result.grades = gradePoints.gradePoints
      result.numCourses = gradePoints.numCourses

      // Calculate average points
      result.average = result.grades / 10

      // Calculate age and age points
      const agepoints = calculateAgePoints(
        result.age,
        userDetails.studyStartYear
      )
      if (diploma === '235') {
        result.age_normal = agepoints.old
      } else {
        result.age_normal = agepoints.normal
      }

      if (diploma === 'Tjuetrefem') {
        result.age_normal = result.age_235
      }

      // Calculate extra points
      result.extra = calculateExtraPoints(extraPoints)

      // Calculate science points
      result.science = calculateSciencePoints(coursesToCalculate)

      // Calculate language points
      result.language = calculateLanguagePoints(coursesToCalculate)

      // Calculate total extra course points
      result.totalScienceAndLanguage = calculateTotalScienceAndLanguagePoints(
        result.language,
        result.science
      )

      // Calculate total points
      const totalpoints = calculateTotalPoints(result)
      result.total_first = totalpoints.first
      result.total_normal = totalpoints.normal
      result.total_235 = totalpoints.old

      span.setAttribute('total_points_normal', result.total_normal)
      span.setAttribute('total_points_first', result.total_first)

      return result
    }
  )
}

export default calculator
